import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Heart, Lock, Eye, EyeOff, Shield, Users, Globe, Zap, CheckCircle, Star, TrendingUp, BadgeCheck } from 'lucide-react';

// SECURITY: Password gate is controlled by environment
// In production, this gate can be disabled via backend config
const STORAGE_KEY = 'fth_site_access';
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PasswordGate = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [gateEnabled, setGateEnabled] = useState(true);

  useEffect(() => {
    // Check if gate is enabled and if already authenticated
    const checkGateStatus = async () => {
      try {
        // First check localStorage for existing auth
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored === 'true') {
          setIsAuthenticated(true);
          setLoading(false);
          return;
        }
        
        // Check if gate is enabled via backend
        const response = await fetch(`${API_URL}/api/config/preview-gate`);
        if (response.ok) {
          const data = await response.json();
          if (!data.enabled) {
            // Gate is disabled in production - bypass
            setGateEnabled(false);
            setIsAuthenticated(true);
          }
        }
      } catch (e) {
        // If we can't reach the backend, assume gate is enabled for safety
        console.log('Preview gate check failed, defaulting to enabled');
      }
      setLoading(false);
    };
    
    checkGateStatus();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      // Validate password via backend
      const response = await fetch(`${API_URL}/api/config/preview-gate/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.valid) {
          localStorage.setItem(STORAGE_KEY, 'true');
          setIsAuthenticated(true);
        } else {
          setError('Incorrect password. Please try again.');
          setPassword('');
        }
      } else {
        setError('Unable to verify password. Please try again.');
      }
    } catch (e) {
      setError('Connection error. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // If gate is disabled or user is authenticated, show children
  if (!gateEnabled || isAuthenticated) {
    return children;
  }

  const features = [
    { icon: Shield, text: '0% Platform Fee', highlight: true },
    { icon: CheckCircle, text: 'Verified Campaigns' },
    { icon: Users, text: 'Secure Payments' },
    { icon: Zap, text: 'AI-Powered Tools' },
    { icon: Globe, text: 'Multi-Language Support' },
    { icon: TrendingUp, text: 'Fast Fund Release' },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0f] flex">
      {/* Left Side - Branding & Info */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/20 via-teal-500/10 to-emerald-500/20" />
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-45"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?w=1200&q=80')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f] via-[#0a0a0f]/50 to-transparent" />
        
        <div className="relative z-10 flex flex-col justify-between p-12 h-full">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-500/20">
              <Heart className="w-7 h-7 text-white" />
            </div>
            <div>
              <span className="text-2xl font-bold text-white">FundTheHope</span>
              <p className="text-emerald-400/80 text-sm italic">"Be the change you wish to see"</p>
            </div>
          </div>

          {/* Main Content */}
          <div className="max-w-lg">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-500/20 to-teal-500/20 rounded-full px-4 py-2 mb-6 border border-emerald-500/30">
              <Star className="w-4 h-4 text-yellow-400" />
              <span className="text-sm text-white/90">Launching Soon • Early Access Preview</span>
            </div>
            
            <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
              Where Every <span className="bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">Dream</span> Finds <span className="bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent">Support</span>
            </h1>
            
            <p className="text-lg text-white/70 mb-8 leading-relaxed">
              The most trusted crowdfunding platform that puts creators first. 
              Launch campaigns, connect with donors, and turn your vision into reality — with zero platform fees.
            </p>

            {/* Feature Pills */}
            <div className="grid grid-cols-2 gap-3 mb-10">
              {features.map((feature, index) => (
                <div 
                  key={index}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 ${
                    feature.highlight 
                      ? 'bg-gradient-to-r from-emerald-500/30 to-teal-500/30 border border-emerald-400/50' 
                      : 'bg-white/5 border border-white/10'
                  }`}
                >
                  <feature.icon className={`w-5 h-5 ${feature.highlight ? 'text-emerald-400' : 'text-white/70'}`} />
                  <span className={`text-sm font-medium ${feature.highlight ? 'text-white' : 'text-white/70'}`}>
                    {feature.text}
                  </span>
                </div>
              ))}
            </div>

            {/* Testimonial */}
            <div className="bg-white/5 rounded-2xl p-5 border border-white/10">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-400/30 to-teal-500/30 flex items-center justify-center border border-emerald-400/30">
                  <Heart className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <p className="text-white/80 text-sm italic mb-2">
                    "No one has ever become poor by giving. Together, we can make a difference in someone's life."
                  </p>
                  <p className="text-white/50 text-xs">— Anne Frank</p>
                </div>
              </div>
            </div>
          </div>

          {/* Vision Statement */}
          <div className="text-center">
            <p className="text-white/60 text-sm italic">
              "Empowering dreams, one donation at a time"
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Password Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-14 h-14 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-2xl flex items-center justify-center">
                <Heart className="w-7 h-7 text-white" />
              </div>
            </div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-emerald-400 to-teal-500 bg-clip-text text-transparent">
              FundTheHope
            </h1>
            <p className="text-emerald-400/80 mt-1 text-sm italic">"Small acts, when multiplied, change the world"</p>
          </div>

          {/* Mobile Feature Highlight */}
          <div className="lg:hidden mb-8">
            <div className="flex items-center justify-center gap-4 flex-wrap">
              <div className="flex items-center gap-2 bg-gradient-to-r from-emerald-500/20 to-teal-500/20 rounded-full px-3 py-1.5 border border-emerald-500/30">
                <Shield className="w-4 h-4 text-emerald-400" />
                <span className="text-xs text-white font-medium">0% Platform Fee</span>
              </div>
              <div className="flex items-center gap-2 bg-white/5 rounded-full px-3 py-1.5 border border-white/10">
                <CheckCircle className="w-4 h-4 text-white/70" />
                <span className="text-xs text-white/70">Secure Platform</span>
              </div>
            </div>
          </div>

          {/* Password Form Card */}
          <div className="bg-[#12121a] rounded-3xl p-8 shadow-2xl border border-white/5">
            <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-r from-emerald-500/20 to-teal-600/20 rounded-2xl mx-auto mb-6">
              <Lock className="w-8 h-8 text-emerald-400" />
            </div>
            
            <h2 className="text-2xl font-bold text-center text-white mb-2">
              Exclusive Preview
            </h2>
            <p className="text-slate-400 text-center mb-6 text-sm">
              Enter your access code to explore the platform before public launch.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter access code"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pr-10 py-6 text-center text-lg tracking-widest bg-[#1a1a24] border-white/10 text-white placeholder:text-slate-500 rounded-xl focus:border-emerald-500/50 focus:ring-emerald-500/20"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>

              {error && (
                <p className="text-red-400 text-sm text-center bg-red-500/10 py-2 px-4 rounded-lg">{error}</p>
              )}

              <Button
                type="submit"
                className="w-full py-6 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white font-semibold text-lg rounded-xl shadow-lg shadow-emerald-500/25 transition-all duration-200"
              >
                Access Platform
              </Button>
            </form>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/10"></div>
              </div>
              <div className="relative flex justify-center">
                <span className="px-4 text-xs text-slate-500 bg-[#12121a]">TRUSTED BY</span>
              </div>
            </div>

            {/* Trust Badges */}
            <div className="flex items-center justify-center gap-6 opacity-60">
              <div className="text-center">
                <div className="flex items-center justify-center w-10 h-10 bg-white/5 rounded-lg mb-1">
                  <Shield className="w-5 h-5 text-white/50" />
                </div>
                <span className="text-[10px] text-white/40">256-bit SSL</span>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center w-10 h-10 bg-white/5 rounded-lg mb-1">
                  <BadgeCheck className="w-5 h-5 text-white/50" />
                </div>
                <span className="text-[10px] text-white/40">Verified</span>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center w-10 h-10 bg-white/5 rounded-lg mb-1">
                  <Globe className="w-5 h-5 text-white/50" />
                </div>
                <span className="text-[10px] text-white/40">Global</span>
              </div>
            </div>
          </div>
          
          {/* AI Mission Statement */}
          <div className="bg-[#12121a]/80 rounded-2xl p-6 mt-6 border border-white/5 max-w-md">
            <p className="text-emerald-400 font-medium mb-4 text-center">
              We're using AI not to replace human trust, but to protect it:
            </p>
            <ul className="space-y-2 text-slate-400 text-sm">
              <li className="flex items-start gap-2">
                <span className="text-emerald-500 mt-0.5">✓</span>
                <span>Safeguarding donors from fraud and misuse</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-emerald-500 mt-0.5">✓</span>
                <span>Helping campaign creators share their stories honestly and clearly</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-emerald-500 mt-0.5">✓</span>
                <span>Elevating verified, high-impact causes</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-emerald-500 mt-0.5">✓</span>
                <span>Making generosity safer, faster, and more transparent</span>
              </li>
            </ul>
            
            <div className="mt-5 pt-5 border-t border-white/10 text-center">
              <p className="text-slate-300 text-sm leading-relaxed">
                <span className="text-white font-medium">FundTheHope</span> is for the creators who dare to ask for help.<br />
                For the donors who give with intention.<br />
                And for everyone who believes technology should serve people — not profit from their hardship.
              </p>
            </div>
            
            <div className="mt-4 text-center">
              <p className="text-slate-400 text-xs">
                If you'd like to be part of the beta, support the mission, or help shape what comes next,
              </p>
              <a href="mailto:support@fundthehope.com" className="inline-flex items-center gap-2 mt-2 text-emerald-400 hover:text-emerald-300 transition-colors text-sm font-medium">
                <span>📩</span> reach out at support@fundthehope.com
              </a>
            </div>
          </div>

          {/* Footer Text */}
          <div className="text-center mt-6">
            <p className="text-slate-500 text-sm">
              Questions? Contact us at{' '}
              <a href="mailto:support@fundthehope.com" className="text-emerald-400 hover:text-emerald-300 transition-colors">
                support@fundthehope.com
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PasswordGate;
